import type { EventPageClass, EventPageEvent, EventPageExtra, SignupState } from '../data/types'
import { truthy } from '../data/types'
import { formatDate, formatDateTime, formatMoney } from '../data/format'
import { unpaidEntryLabel, unpaidEntrySummary } from '../data/signupSummary'
import styles from './RegistrationCard.module.css'

/** One line of the "Your entry" list below - a single entered class or added extra, resolved
 *  against the plain `classes`/`extras` arrays for its display name (SignupClassState/
 *  SignupExtraState only carry an id - see types.ts's own comment on why the racer-specific
 *  signup snapshot is fetched separately from the plain event/class/extra data). */
interface EntryRow {
  key: string
  name: string
  detail: string
  paid: boolean
  amount: number | string
}

function buildEntryRows(signup: SignupState | null, classes: EventPageClass[], extras: EventPageExtra[]): EntryRow[] {
  if (!signup) return []
  const rows: EntryRow[] = []
  for (const row of signup.classes) {
    const entered = row.enteredVehicleId !== '' && row.enteredVehicleId !== null && row.enteredVehicleId !== undefined
    if (!entered) continue
    const cls = classes.find((c) => c.id === row.id)
    const vehicle = signup.vehicles.find((v) => v.id === Number(row.enteredVehicleId))
    rows.push({
      key: `class-${row.id}`,
      name: cls ? cls.name : 'Class',
      detail: `Car: ${vehicle ? vehicle.name : 'not chosen'}`,
      paid: truthy(row.paid),
      amount: row.amountDue,
    })
  }
  for (const row of signup.extras) {
    if (!truthy(row.added)) continue
    const extra = extras.find((e) => e.id === row.id)
    rows.push({
      key: `extra-${row.id}`,
      name: extra ? extra.name : 'Add-on',
      detail: row.optionLabel || '',
      paid: truthy(row.paid),
      amount: row.amount,
    })
  }
  return rows
}

export function RegistrationCard({
  event,
  classes,
  extras,
  signup,
  isMobile,
  onGotoClasses,
}: {
  event: EventPageEvent
  classes: EventPageClass[]
  extras: EventPageExtra[]
  signup: SignupState | null
  // Desktop only - mobile already surfaces this same information via ClassList's own top-of-list
  // entry status summary card plus each row's inline car pill/badge, and the mobile registration
  // card is pinned right at the hero (not alongside the Classes list the way desktop's rail is),
  // so repeating a full itemized list there would just be redundant clutter.
  isMobile: boolean
  onGotoClasses: () => void
}) {
  const open = truthy(event.signupWindowOpen)
  const full = truthy(event.full)
  const usingRacerLimit = event.racerLimit !== '' && event.racerLimit !== null && event.racerLimit !== undefined
  const limit = Number(usingRacerLimit ? event.racerLimit : event.entryLimit) || 0
  const count = Number(usingRacerLimit ? event.racerCount : event.entryCount) || 0
  const pct = limit > 0 ? Math.min(100, Math.round((count / limit) * 100)) : 0

  const tiers: Array<[string, number | string]> = [
    ['1st class', event.classCost1],
    ['2nd', event.classCost2],
    ['3rd', event.classCost3],
    ['4th+', event.classCost4],
  ].filter(([, v]) => formatMoney(v, event.dollarType || 'USD') !== '') as Array<[string, number | string]>

  const summary = unpaidEntrySummary(signup)
  const summaryLabel = unpaidEntryLabel(summary)
  const entryRows = isMobile ? [] : buildEntryRows(signup, classes, extras)

  let statusLabel = 'Registration open'
  let statusOk = true
  if (!open) {
    statusLabel = 'Registration closed'
    statusOk = false
  } else if (full) {
    statusLabel = 'Event full'
    statusOk = false
  }

  return (
    <aside className={styles.card}>
      <div className={styles.status}>
        <span className={`${styles.dot} ${statusOk ? styles.dotOpen : styles.dotClosed}`} />
        {statusLabel}
      </div>

      {limit > 0 ? (
        <div className={styles.progressBlock}>
          <div className={styles.progressTrack}>
            <div className={styles.progressFill} style={{ width: `${pct}%` }} />
          </div>
          <div className={styles.progressLabel}>
            <span>
              {count} of {limit} entries
            </span>
          </div>
        </div>
      ) : null}

      <div className={styles.dates}>
        {event.entryLineDT ? (
          <div className={styles.dateRow}>
            <span>Payment due</span>
            <strong>{formatDate(event.entryLineDT)}</strong>
          </div>
        ) : null}
        {event.closePaymentDT ? (
          <div className={styles.dateRow}>
            <span>Registration closes</span>
            <strong>{formatDateTime(event.closePaymentDT)}</strong>
          </div>
        ) : null}
      </div>

      {tiers.length ? (
        <div className={styles.tiers}>
          {tiers.map(([label, value]) => (
            <div key={label} className={styles.tier}>
              <div className={styles.tierLabel}>{label}</div>
              <div className={styles.tierValue}>{formatMoney(value, event.dollarType || 'USD')}</div>
            </div>
          ))}
        </div>
      ) : null}

      {/* "Your entry" - Desktop.dc.html's own itemized list (ported here, desktop-only - see the
          isMobile comment on the prop above), each entered class/add-on with its paid/unpaid
          status, so the racer can see exactly what they're registered for without leaving the
          rail. summaryLabel/priceLine below already covers the unpaid total; this covers the
          per-item detail that total doesn't show. */}
      {entryRows.length ? (
        <div className={styles.entryList}>
          <div className={styles.entryListLabel}>Your entry</div>
          {entryRows.map((row) => (
            <div key={row.key} className={styles.entryRow}>
              <div className={styles.entryRowMain}>
                <span className={styles.entryName}>{row.name}</span>
                {row.detail ? <span className={styles.entryDetail}>{row.detail}</span> : null}
              </div>
              <span className={`${styles.entryBadge} ${row.paid ? styles.entryBadgePaid : styles.entryBadgeUnpaid}`}>
                {row.paid ? `✓ Paid · ${formatMoney(row.amount, event.dollarType || 'USD')}` : formatMoney(row.amount, event.dollarType || 'USD') ? `Unpaid · ${formatMoney(row.amount, event.dollarType || 'USD')}` : 'Unpaid'}
              </span>
            </div>
          ))}
        </div>
      ) : null}

      {summaryLabel ? (
        <div className={styles.priceLine}>
          <span className={styles.priceLabel}>{summaryLabel} selected</span>
          <span className={styles.priceTotal}>{formatMoney(summary.total, event.dollarType || 'USD')}</span>
        </div>
      ) : null}

      {open && !full ? (
        signup ? (
          signup.totalEntries > 0 ? (
            <a href={signup.checkoutUrl} className={styles.signupBtn}>
              Go to checkout
            </a>
          ) : (
            // A logged-in racer signs up inline in the Classes section now (checkbox -> vehicle
            // popup, see ClassList) rather than following signupUrl out to the legacy page.
            <button type="button" className={styles.signupBtn} onClick={onGotoClasses}>
              Sign up
            </button>
          )
        ) : (
          <a href={event.signupUrl} className={styles.signupBtn}>
            Sign up
          </a>
        )
      ) : (
        <span className={`${styles.signupBtn} ${styles.signupBtnDisabled}`}>{full ? 'Event full' : 'Registration closed'}</span>
      )}
    </aside>
  )
}
